import { Editor } from '@tiptap/core';
import { EditorState, Transaction } from '@tiptap/pm/state';

import { findSuggestionMatch } from '../find-suggestion-match';
import { createInitialState, SuggestionState } from './create-initial-state';

export const suggestionEscapeMetaKey = (name: string): string => `${name}SuggestionEscape`;

interface ApplyTransactionOptions {
	allowMultiple?: boolean;
	char: string;
	editor: Editor;
	matchQuery: RegExp | RegExp[];
	matchWithoutTrigger: boolean;
	name: string;
	newState: EditorState;
	prevState: SuggestionState;
	transaction: Transaction;
}

export const applyTransaction = ({
	allowMultiple = true,
	char,
	editor,
	matchQuery,
	matchWithoutTrigger = false,
	name,
	newState,
	prevState,
	transaction,
}: ApplyTransactionOptions): SuggestionState => {
	const { isEditable } = editor;
	const { composing } = editor.view;

	if (transaction.getMeta(suggestionEscapeMetaKey(name))) {
		return { ...createInitialState(), composing };
	}

	const { selection } = newState;
	const { empty, from } = selection;
	const next = { ...prevState };

	next.composing = composing;

	// We can only be suggesting if the view is editable, and:
	//   * there is no selection, or
	//   * a composition is active (see: https://github.com/ueberdosis/tiptap/issues/1449)
	if (isEditable && (empty || editor.view.composing)) {
		// Reset active state if we just left the previous suggestion range
		if ((from < prevState.range.from || from > prevState.range.to) && !composing && !prevState.composing) {
			next.active = false;
		}

		// Try to match against where our cursor currently is
		const match = findSuggestionMatch({
			char,
			matchQuery,
			matchWithoutTrigger,
			$position: selection.$from,
		});

		const decorationId = `id_${Math.floor(Math.random() * 0xffffffff)}`;

		// If we found a match, update the current state to show it
		if (match) {
			let allow = true;

			// Must use newState: editor.state is still pre-transaction during apply.
			if (!allowMultiple) {
				newState.doc.descendants((node) => {
					if (node.type.name === name) {
						allow = false;
					}
				});
			}

			if (allow) {
				Object.assign(
					next,
					{
						active: true,
						decorationId: prevState.decorationId ? prevState.decorationId : decorationId,
					},
					match
				);
			} else {
				next.active = false;
			}
		} else {
			next.active = false;
		}
	} else {
		next.active = false;
	}

	// Make sure to empty the range if suggestion is inactive
	if (!next.active) {
		Object.assign(next, {
			decorationId: null,
			range: { from: 0, to: 0 },
			query: null,
			text: null,
		});
	}

	return next;
};
